import React, { useState } from 'react';
import {Button, CustomTextField, Label, Loading} from '../../../index';
import {useHistory} from 'react-router';
import {Trans, useTranslation} from 'react-i18next';
import i18nNamespaces from '../../../../const/i18nNamespaces';
import {NEW_PRESCRIPTION_DRAFT_PAGE, QUESTIONNAIRE_PAGE} from '../../../../const/routes';
import useStylesCommon from '../common/AdditionalDetailsCommonStyles';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { Box, FormGroup } from '@material-ui/core';
import accountProvider from '../../../../services/accountProvider';
import { error } from 'console';
import { confirmIdentityErrorHandler } from '../../../../utils/error';
import axios from 'axios';

const REQUIRED_STRING = 'requiredField';

interface IdentityConfirmationPromptProps {
    onSuccess: () => void;
}

const IdentityConfirmationPrompt: React.FC<IdentityConfirmationPromptProps> = ({
    onSuccess
}) => {
    const {t} = useTranslation(i18nNamespaces.APPOINTMENT_DETAILS);
    const {t: authT} = useTranslation(i18nNamespaces.AUTH);
    const {t: accountT} = useTranslation(i18nNamespaces.ACCOUNT);
    const classesCommon = useStylesCommon();
    const [errorMessages, setErrorMessages] = useState<string[] | null>(null);
    const [loading, setLoading] = useState(false);


    const peselValidation = yup
            .string()
            .matches(/^[0-9]+$/, authT('peselShouldContainOnlyNumbers'))
            .length(11, authT('lengthShouldBe11chars'));
    
        const {
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
            setValues,
            handleSubmit,
            isSubmitting
        } = useFormik({
            initialValues: {
                name: '',
                secondName: '',
                surname: '',
                pesel: '',
            },
            validationSchema: yup.object({
                name: yup.string().required(t(REQUIRED_STRING)),
                surname: yup.string().required(t(REQUIRED_STRING)),
                pesel: peselValidation.required(t(REQUIRED_STRING)),
            }),
            onSubmit: async (values, helpers) => {
                setLoading(true);
                setErrorMessages(null);
                try {
                    await accountProvider.confirmIdentity({
                        name: values.name,
                        secondName: values.secondName,
                        surname: values.surname,
                        pesel: values.pesel,
                    });
                    onSuccess();
                } catch (error) {
                    if(axios.isAxiosError(error)){
                        const errorArray = error.response?.data?.map((err: string) => accountT(confirmIdentityErrorHandler(err)));
                        setErrorMessages(errorArray || [accountT('errorOccuredCheckIfDataIsCorrect')]);
                    }else {
                        setErrorMessages([accountT('errorOccuredCheckIfDataIsCorrect')]);
                    }
                } finally {
                    setLoading(false);
                    helpers.setSubmitting(false);
                }
            },
        });

    return (
        <>
            <Loading loading={loading} withBackdrop />
            <h4 className={classesCommon.detailsHeader}>
                {t('identityConfirmation.header')}
            </h4>
            <p>
                <Trans t={t} i18nKey="identityConfirmation.description" />
            </p>
            <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                <FormGroup>
                    <Label htmlFor="name">
                        {authT('name')}*
                    </Label>
                    <CustomTextField
                        fullWidth
                        id="name"
                        name="name"
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                        helperText={
                            touched.name && authT(errors.name)
                        }
                        error={touched.name && !!errors.name}
                    />
                </FormGroup>
                <FormGroup>
                    <Label htmlFor="surname">
                        {authT('surname')}*
                    </Label>
                    <CustomTextField
                        variant="outlined"
                        fullWidth
                        id="surname"
                        name="surname"
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.surname}
                        helperText={
                            touched.surname && authT(errors.surname)
                        }
                        error={touched.surname && !!errors.surname}
                    />
                </FormGroup>
                <FormGroup>
                    <Label htmlFor="pesel">
                        PESEL*
                    </Label>
                    <CustomTextField
                        variant="outlined"
                        fullWidth
                        id="pesel"
                        name="pesel"
                        type="text"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.pesel}
                        helperText={
                            touched.pesel && authT(errors.pesel)
                        }
                        error={touched.pesel && !!errors.pesel}
                    />
                </FormGroup>
                {errorMessages && errorMessages.length > 0 && 
                    errorMessages.map((error, index) => (
                        <Box key={index} style={{color: 'red'}}>
                            {error}
                        </Box>
                ))}
                
                <Button
                    variant="contained"
                    color="secondary"
                    type="submit"
                    disabled={isSubmitting}
                    style={{marginTop: 16}}
                    fullWidth
                >
                    {t('identityConfirmation.buttonLabel')}
                </Button>
            </form>
        </>
    );
};

export default IdentityConfirmationPrompt;

